/**
 * Trace query hooks
 * React Query hooks for fetching trace/span data from the admin API
 */

import { useQuery } from '@tanstack/react-query';
import { tracesApi } from '@/lib/api/traces';
import type { TraceListParams, TraceMetricsParams } from '@/types/traces';

export function useTracesStatus() {
  return useQuery({
    queryKey: ['traces', 'status'],
    queryFn: () => tracesApi.getStatus(),
    staleTime: 60_000,
  });
}

export function useTraceList(params: TraceListParams = {}) {
  return useQuery({
    queryKey: ['traces', 'list', params],
    queryFn: () => tracesApi.list(params),
    staleTime: 30_000,
  });
}

export function useTraceDetail(traceId: string | null) {
  return useQuery({
    queryKey: ['traces', 'detail', traceId],
    queryFn: () => tracesApi.get(traceId!),
    enabled: !!traceId,
  });
}

export function useTraceSpans(
  traceId: string | null,
  params: { page?: number; size?: number; span_type?: string } = {}
) {
  return useQuery({
    queryKey: ['traces', 'spans', traceId, params],
    queryFn: () => tracesApi.getSpans(traceId!, params),
    enabled: !!traceId,
  });
}

export function useTraceStats(projectName?: string) {
  return useQuery({
    queryKey: ['traces', 'stats', projectName],
    queryFn: () => tracesApi.getStats(projectName),
    staleTime: 60_000,
  });
}

export function useTraceMetrics(params: TraceMetricsParams = {}) {
  return useQuery({
    queryKey: ['traces', 'metrics', params],
    queryFn: () => tracesApi.getMetrics(params),
    staleTime: 60_000,
  });
}
